import { Menu, Text } from '@mantine/core'

import { ApplicantStage } from 'src/graphql/types/applicants'
import { AutomatedRuleActionType } from 'src/graphql/types/automatedRules'
import IconAdd from 'src/icons/IconAdd'
import IconChevronDown from 'src/icons/IconChevronDown'

import { ACTION_TYPE_LABEL } from '../utils/actionDisplay'

import ApplicantStatusOptions from './ApplicantStatusOptions'
import ActionIconBadge from './atoms/ActionIconBadge'
import RuleListRow from './atoms/RuleListRow'
import CourseOptions from './CourseOptions'
import DocumentPackageOptions from './DocumentPackageOptions'
import EmailTemplateOptions from './EmailTemplateOptions'
import LeadEmployeeOptions from './LeadEmployeeOptions'
import MemberEmployeeOptions from './MemberEmployeeOptions'
import NoteTemplateOptions from './NoteTemplateOptions'
import OrderOptions from './OrderOptions'
import SMSTemplateOptions from './SMSTemplateOptions'
import TaskTemplateOptions from './TaskTemplateOptions'
import TrainingOptions from './TrainingOptions'
import VoiceAgentOptions from './VoiceAgentOptions'

// Single source of truth for action type → display label is
// `ACTION_TYPE_LABEL` in utils/actionDisplay.tsx (also consumed by the
// pipeline RuleCard). The drawer uses the same labels so the read view
// and edit view never drift.
const labelOf = (actionType: string): string =>
  ACTION_TYPE_LABEL[actionType] ?? actionType

export const ACTION_KEY_MAP = {
  [AutomatedRuleActionType.CHANGE_APPLICANT_STATUS]: {
    key: AutomatedRuleActionType.CHANGE_APPLICANT_STATUS,
    label: labelOf(AutomatedRuleActionType.CHANGE_APPLICANT_STATUS),
    menuGroup: 'Applicant Management',
    description: 'Change the applicant status',
    isValidAction: (action) => {
      // Has applicant stage and based on the applicant stage, has the required fields
      return (
        !!action?.applicantStage &&
        {
          [ApplicantStage.HIRED]: true,
          [ApplicantStage.PROSPECT]: !!action?.hiringStage,
          [ApplicantStage.TERMINATED]: !!action?.terminatedReason,
          [ApplicantStage.DISQUALIFIED]: !!action?.disqualifiedReason,
          [ApplicantStage.NOT_INTERESTED]: !!action?.notInterestedReason,
        }[action?.applicantStage]
      )
    },
    component: (action, setAction) => (
      <ApplicantStatusOptions
        applicantStage={action}
        setApplicantStage={setAction}
      />
    ),
  },
  [AutomatedRuleActionType.CREATE_TASK]: {
    key: AutomatedRuleActionType.CREATE_TASK,
    label: labelOf(AutomatedRuleActionType.CREATE_TASK),
    menuGroup: 'Applicant Management',
    description: 'Create a Task for the applicant',
    isValidAction: (action) => !!action?.taskTemplateId,
    component: (action, setAction) => (
      <TaskTemplateOptions
        selectedTaskTemplate={action}
        setSelectedTaskTemplate={setAction}
      />
    ),
  },
  [AutomatedRuleActionType.ADD_NOTE]: {
    key: AutomatedRuleActionType.ADD_NOTE,
    label: labelOf(AutomatedRuleActionType.ADD_NOTE),
    menuGroup: 'Applicant Management',
    description: "Add a note from a note template to the applicant's profile",
    isValidAction: (action) => !!action?.noteTemplateId,
    component: (action, setAction) => (
      <NoteTemplateOptions
        selectedNoteTemplate={action}
        setSelectedNoteTemplate={setAction}
      />
    ),
  },
  [AutomatedRuleActionType.REASSIGN_LEAD_EMPLOYEE]: {
    key: AutomatedRuleActionType.REASSIGN_LEAD_EMPLOYEE,
    label: labelOf(AutomatedRuleActionType.REASSIGN_LEAD_EMPLOYEE),
    menuGroup: 'Applicant Management',
    description: 'Reassign the lead employee for the applicant',
    isValidAction: (action) => !!action?.leadEmployeeId,
    component: (action, setAction) => (
      <LeadEmployeeOptions action={action} setSelected={setAction} />
    ),
  },
  [AutomatedRuleActionType.ADD_MEMBER_EMPLOYEE]: {
    key: AutomatedRuleActionType.ADD_MEMBER_EMPLOYEE,
    label: labelOf(AutomatedRuleActionType.ADD_MEMBER_EMPLOYEE),
    menuGroup: 'Applicant Management',
    description: 'Add member employees to the applicant',
    isValidAction: (action) => (action?.memberEmployeeIds?.length ?? 0) > 0,
    component: (action, setAction) => (
      <MemberEmployeeOptions action={action} setSelected={setAction} />
    ),
  },
  [AutomatedRuleActionType.SEND_VOICE_AGENT_CALL]: {
    key: AutomatedRuleActionType.SEND_VOICE_AGENT_CALL,
    label: labelOf(AutomatedRuleActionType.SEND_VOICE_AGENT_CALL),
    menuGroup: 'Communication',
    description: 'Send a voice agent call to the applicant',
    isValidAction: (action) => !!action?.voiceAgentId,
    component: (action, setAction) => (
      <VoiceAgentOptions
        selectedVoiceAgent={action}
        setSelectedVoiceAgent={setAction}
      />
    ),
  },
  [AutomatedRuleActionType.SEND_EMAIL]: {
    key: AutomatedRuleActionType.SEND_EMAIL,
    label: labelOf(AutomatedRuleActionType.SEND_EMAIL),
    menuGroup: 'Communication',
    description: 'Send an email to the applicant',
    // make sure emailTemplateId exists and is not null
    isValidAction: (action) => !!action?.emailTemplateId,
    component: (action, setAction) => (
      <EmailTemplateOptions
        selectedEmailTemplate={action}
        setSelectedEmailTemplate={setAction}
      />
    ),
  },
  [AutomatedRuleActionType.SMS]: {
    key: AutomatedRuleActionType.SMS,
    label: labelOf(AutomatedRuleActionType.SMS),
    menuGroup: 'Communication',
    description: 'Send an SMS to the applicant',
    // Return true if textTemplateId exists and is not null
    isValidAction: (action) => !!action?.textTemplateId,
    component: (action, setAction) => (
      <SMSTemplateOptions
        selectedSmsTemplate={action}
        setSelectedSmsTemplate={setAction}
      />
    ),
  },
  [AutomatedRuleActionType.DOCUMENT_TEMPLATE_PACKAGE]: {
    key: AutomatedRuleActionType.DOCUMENT_TEMPLATE_PACKAGE,
    label: labelOf(AutomatedRuleActionType.DOCUMENT_TEMPLATE_PACKAGE),
    menuGroup: 'Documents & Orders',
    description: 'Send documents for signing to the applicant',
    isValidAction: (action) => !!action?.documentTemplatePackageId,
    component: (action, setAction) => (
      <DocumentPackageOptions
        selectedDocumentTemplatePackage={action}
        setSelectedDocumentTemplatePackage={setAction}
      />
    ),
  },
  [AutomatedRuleActionType.GENERATE_ORDER]: {
    key: AutomatedRuleActionType.GENERATE_ORDER,
    label: labelOf(AutomatedRuleActionType.GENERATE_ORDER),
    menuGroup: 'Documents & Orders',
    description: 'Request an order to the applicant',
    isValidAction: (action) => !!action?.packageId,
    component: (action, setAction) => (
      <OrderOptions
        selectedOrder={action}
        setSelectedOrder={setAction}
        fcraLegalRequirementsConsent={action?.fcraLegalRequirementsConsent}
        setFcraLegalRequirementsConsent={(fcraLegalRequirementsConsent) =>
          setAction({ ...action, fcraLegalRequirementsConsent })
        }
      />
    ),
  },
  [AutomatedRuleActionType.ASSIGN_COURSE]: {
    key: AutomatedRuleActionType.ASSIGN_COURSE,
    label: labelOf(AutomatedRuleActionType.ASSIGN_COURSE),
    menuGroup: 'Learning & Training',
    description: 'Assign a course to the applicant',
    isValidAction: (action) => !!action?.courseId,
    component: (action, setAction) => (
      <CourseOptions selectedCourse={action} setSelectedCourse={setAction} />
    ),
  },
  [AutomatedRuleActionType.ASSIGN_TRAINING]: {
    key: AutomatedRuleActionType.ASSIGN_TRAINING,
    label: labelOf(AutomatedRuleActionType.ASSIGN_TRAINING),
    menuGroup: 'Learning & Training',
    description: 'Assign a lesson to the applicant',
    isValidAction: (action) => !!action?.trainingId,
    component: (action, setAction) => (
      <TrainingOptions selectedLesson={action} setSelectedLesson={setAction} />
    ),
  },
  [AutomatedRuleActionType.START_VOE_OUTREACH]: {
    key: AutomatedRuleActionType.START_VOE_OUTREACH,
    label: labelOf(AutomatedRuleActionType.START_VOE_OUTREACH),
    menuGroup: 'Employment Verification',
    description:
      'Start the automated outreach sequence for all open VoEs on this applicant',
    isValidAction: () => true,
    component: () => (
      <Text size="xs" className="text-doubleNickel-gray-500">
        Starts the automated email sequence for all active outbound VoEs on this
        applicant.
      </Text>
    ),
  },
}

const AutomatedRuleActions = ({ actions = [], setActions }) => {
  const actionGroups = Object.keys(ACTION_KEY_MAP).reduce((acc, key) => {
    const action = ACTION_KEY_MAP[key]
    if (!acc[action.menuGroup]) {
      acc[action.menuGroup] = []
    }
    acc[action.menuGroup].push(action)
    return acc
  }, {})

  const updateAction = (index, key, action) => {
    const updatedActions = actions.map((existingAction, existingIndex) => {
      if (existingIndex !== index) {
        return existingAction
      }

      return {
        ...action,
        actionType: key,
      }
    })

    setActions(updatedActions)
  }

  const onActionTypeChange = (index, key) => {
    const updatedActions = actions.map((action, actionIndex) =>
      actionIndex === index
        ? {
            actionType: key,
          }
        : action
    )

    setActions(updatedActions)
  }

  const addAction = (key) => {
    setActions([
      ...actions,
      {
        actionType: key,
      },
    ])
  }

  const removeAction = (index) => {
    setActions(actions.filter((_, actionIndex) => actionIndex !== index))
  }

  const renderChangeTypeMenu = (index, currentLabel) => (
    <Menu shadow="md" position="bottom-start" withinPortal>
      <Menu.Target>
        <button
          type="button"
          className="inline-flex items-center gap-1 bg-transparent p-0 text-[12px] font-semibold leading-tight text-doubleNickel-gray-900 transition-colors hover:text-doubleNickel-brand-500"
        >
          <span>{currentLabel}</span>
          <IconChevronDown className="h-3 w-3 fill-none stroke-current" />
        </button>
      </Menu.Target>
      <Menu.Dropdown className="max-h-[40vh] overflow-y-auto">
        {Object.keys(actionGroups).map((group) => (
          <div key={group}>
            <Menu.Label>{group}</Menu.Label>
            {actionGroups[group].map((actionOption) => (
              <Menu.Item
                key={actionOption.key}
                className="text-xs"
                onClick={() => onActionTypeChange(index, actionOption.key)}
              >
                {actionOption.label}
              </Menu.Item>
            ))}
          </div>
        ))}
      </Menu.Dropdown>
    </Menu>
  )

  const actionsHeader = (
    <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
      ACTIONS
    </Text>
  )

  const addActionMenu = (
    // `width="target"` makes the dropdown match the full-width pill — same
    // treatment as the Add Condition menu, so both affordances feel paired.
    <Menu shadow="md" position="bottom-start" width="target" withinPortal>
      <Menu.Target>
        <button
          type="button"
          className="flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-doubleNickel-gray-300 bg-transparent px-2.5 py-2 text-[11.5px] font-medium leading-tight text-doubleNickel-gray-500 transition-colors hover:border-doubleNickel-brand-500 hover:bg-[rgba(28,147,215,0.04)] hover:text-doubleNickel-brand-500"
        >
          <IconAdd className="h-3 w-3 fill-none stroke-current" />
          <span>Add Action</span>
        </button>
      </Menu.Target>
      <Menu.Dropdown className="max-h-[40vh] overflow-y-auto">
        {Object.keys(actionGroups).map((group) => (
          <div key={group}>
            <Menu.Label key={group}>{group}</Menu.Label>
            {actionGroups[group].map((action) => (
              <Menu.Item
                key={action.key}
                className="text-xs"
                onClick={() => addAction(action.key)}
              >
                {action.label}
              </Menu.Item>
            ))}
          </div>
        ))}
      </Menu.Dropdown>
    </Menu>
  )

  return (
    <div className="space-y-2.5">
      {actionsHeader}

      {actions.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          {actions.map((action, index) => {
            const chosenAction =
              ACTION_KEY_MAP[action?.actionType] ||
              ACTION_KEY_MAP[AutomatedRuleActionType.SMS]

            return (
              <RuleListRow
                key={index}
                // ActionIconBadge brings its own per-action-type colored
                // pill — same coloring the pipeline RuleCard uses — so we
                // skip RuleListRow's default chrome with iconKind="custom".
                iconKind="custom"
                icon={<ActionIconBadge action={action} />}
                label={renderChangeTypeMenu(index, chosenAction.label)}
                value={chosenAction.component(action, (updatedAction) =>
                  updateAction(index, chosenAction.key, updatedAction)
                )}
                onRemove={() => removeAction(index)}
              />
            )
          })}
        </div>
      ) : null}

      {addActionMenu}
    </div>
  )
}

export default AutomatedRuleActions
